import * as React from 'react';
import Select, {
	SelectValue,
} from '../../../../../Components/Base/Select/Select';
import {
	PaginationOrderHow,
	useTermItemsQuery,
} from '../../../../../graphql/graphql';
import { FieldError } from 'react-hook-form';
import { useMemo, useState } from 'react';
import Loading from '../../../../../Components/Loading';
import useTranslation from '../../../../../Hooks/useTranslation';
import useSelectValueGuard from '../../../Hooks/useSelectValueGuard';

type TermItemField = 'id' | 'name' | 'value' | 'value2' | 'value3';

interface Props {
	onChange?: (value: SelectValue) => void;
	value?: SelectValue;
	error?: FieldError;
	termType: string;
	returnKey?: string;
	labelFrom?: TermItemField;
	valueFrom?: TermItemField;
	/** Template for label, e.g. '{value} - {name}'. Placeholders: {id}, {name}, {value}, {value2}, {value3} */
	labelFormat?: string;
	showErrorMessage?: boolean;
	sortBy?: string;
}

const TermItemSelect: React.FC<Props> = ({
	value,
	onChange,
	returnKey,
	labelFrom = 'name',
	valueFrom = 'id',
	labelFormat,
	showErrorMessage = false,
	sortBy = 'value',
	termType,
	...props
}) => {
	const { data, isLoading } = useTermItemsQuery({
		termType,
		pagination: {
			take: 999,
			order: { key: sortBy, how: PaginationOrderHow.Asc },
		},
	});
	const [defaultValue, setDefaultValue] = useState(value);
	const { t } = useTranslation();

	const options: SelectValue[] = useMemo(() => {
		const items: SelectValue[] = [];

		for (const termItem of data?.termItems?.data || []) {
			if (termItem) {
				const valueRaw = termItem[valueFrom];
				const label = labelFormat
					? labelFormat.replace(/\{(\w+)\}/g, (_, key) =>
							String(termItem[key as TermItemField] ?? ''),
					  )
					: termItem[labelFrom] ?? termItem.id ?? '';
				items.push({
					value: (valueRaw ?? termItem.id) as string,
					label: label as string,
				});
			}
		}

		return items;
	}, [data, labelFrom, valueFrom, labelFormat]);

	useSelectValueGuard({ options, value, onChange });

	if (isLoading) {
		return <Loading />;
	}

	return (
		<Select
			{...props}
			options={options}
			onChange={onChange}
			placeholder={t('base.shared.pick')}
			value={defaultValue?.label ? defaultValue : value}
			returnKey={returnKey}
			showErrorMessage={showErrorMessage}
		/>
	);
};

export default TermItemSelect;
